import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import { Modal as ModalContainer, Box, useTheme, styled } from '@mui/material';
import { ConfirmationBox } from '@/pages/company/componants/Base';
import { useTranslation } from 'react-i18next';
import { useContext, useState, useEffect } from 'react';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { TipsHistory } from '@/services/PaymentsServiceV2';
import { DATE_FORMAT } from '@/lib/constants';
import { useFormatting } from '@/utils/formatting';
import {
    SMMedium,
    SMRegular,
    SMSemiBold,
    SubTitle
} from '@/typography/Typography';
import { UserContext } from '@/contexts/user/UserContext';
import { NumericInput } from '@/components/shared/NumericInput';
import {
    PrimaryButtonLG,
    SecondaryButtonGrayLG
} from '@/components/shared/Button';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { RadioButton } from '@/components/shared/RadioButton';

const JournalOption = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // width: '402px',
    height: '36px',
    padding: '8px',
    gap: '4px',
    border: `1px solid ${theme.palette.greyBorder}`,
    borderRadius: '8px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    opacity: 1,
    '&:hover': {
        backgroundColor: theme.palette.grey[50],
        borderColor: theme.palette.primary.main
    },
    '&.selected': {
        backgroundColor: theme.palette.primary.lighter || '#F0F7FF',
        borderColor: theme.palette.primary.main
    }
}));

const JournalInfo = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    flex: 1
});

// Cap the journal list to a fraction of the viewport height so every journal
// stays reachable by scrolling on short screens, while the dialog header,
// amount input and action buttons remain visible.
const JournalList = styled(Box)({
    maxHeight: '40vh',
    overflowY: 'auto'
});

interface EditTipsDialogProp {
    editTip: TipsHistory | null;
    onClose?: () => void;
}

// Sentinel selection value for the "redistribute all journals" option — it is
// not a real posJournalId, so it must never collide with one from the backend.
const REDISTRIBUTE_ALL_JOURNALS = 'REDISTRIBUTE_ALL_JOURNALS';

export const EditTipsDialog = ({ editTip, onClose }: EditTipsDialogProp) => {
    const { t } = useTranslation();

    const { updateJournalAmount, redistributeTipsForDate } =
        usePaymentsMutations();

    const [newAmount, setNewAmount] = useState<number | undefined>();
    const [selectedJournalId, setSelectedJournalId] = useState<string>('');

    // Check journal counts
    const hasJournalAmounts =
        editTip?.posJournalAmounts && Array.isArray(editTip.posJournalAmounts);
    const hasSingleJournal =
        hasJournalAmounts && editTip.posJournalAmounts!.length === 1;
    const hasMultipleJournals =
        hasJournalAmounts && editTip.posJournalAmounts!.length > 1;

    // Auto-select first journal if only one exists
    useEffect(() => {
        if (hasSingleJournal) {
            const journal = editTip.posJournalAmounts![0];
            setSelectedJournalId(journal.posJournalId);
        } else if (hasMultipleJournals) {
            // Reset when showing radio buttons
            setSelectedJournalId('');
            setNewAmount(undefined);
            return;
        }
        if (editTip) {
            setNewAmount(editTip.totalTips);
        }
    }, [editTip, hasSingleJournal, hasMultipleJournals]);

    const isRedistributeAll = selectedJournalId === REDISTRIBUTE_ALL_JOURNALS;

    // Handle journal selection from radio button
    const handleJournalChange = (journalId: string) => {
        setSelectedJournalId(journalId);
        const journal = editTip?.posJournalAmounts?.find(
            (j) => j.posJournalId === journalId
        );
        const defaultAmount = journal?.totalTips;
        setNewAmount(defaultAmount);
        updateJournalAmount.reset();
        redistributeTipsForDate.reset();
    };

    const onCloseHandler = () => {
        setNewAmount(undefined);
        setSelectedJournalId('');
        updateJournalAmount.reset();
        redistributeTipsForDate.reset();
        onClose?.();
    };

    const { getBalanceLabel } = useFormatting();
    const { company } = useContext(UserContext);

    useHandleResponse({
        error: null, // handled outside
        isSuccess:
            updateJournalAmount.isSuccess || redistributeTipsForDate.isSuccess,
        successAlertMessage: t('tips.redistribution_successful'),
        isSuccessCallBack: () => {
            onCloseHandler();
        }
    });

    const _theme = useTheme();

    return (
        <ModalContainer open={!!editTip} onClose={onCloseHandler}>
            <ConfirmationBox style={{ width: '450px' }}>
                <ConnectHeader
                    // logo={getLogoForPOSProvider()}
                    title={t('tips.edit_tips_for')}
                    description={t('tips.edit_tips_for_description', {
                        date: editTip?.date?.format(
                            DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR
                        ),
                        locationName: editTip?.locationName
                    })}
                />

                <SMMedium style={{ textAlign: 'center' }} mb={3} mt={2}>
                    {t('tips.current_tips_amount_label')}{' '}
                    <span data-testid="current-tips-amount">
                        {getBalanceLabel(editTip?.totalTips, company?.currency)}
                    </span>
                </SMMedium>

                {hasMultipleJournals && (
                    <Box mb={3}>
                        <SMMedium mb={1.5}>
                            {t('tips.edit_what_journal')}
                        </SMMedium>
                        <JournalList>
                            <JournalOption
                                data-testid="redistribute-all-journals-option"
                                className={isRedistributeAll ? 'selected' : ''}
                                onClick={() =>
                                    handleJournalChange(
                                        REDISTRIBUTE_ALL_JOURNALS
                                    )
                                }
                            >
                                <JournalInfo>
                                    <SMSemiBold color="#667085">
                                        {t(
                                            'tips.redistribute_all_journals_same_amount'
                                        )}
                                    </SMSemiBold>
                                </JournalInfo>
                                <RadioButton
                                    checked={isRedistributeAll}
                                    onChange={() =>
                                        handleJournalChange(
                                            REDISTRIBUTE_ALL_JOURNALS
                                        )
                                    }
                                    size="small"
                                    customRadio
                                />
                            </JournalOption>
                            {editTip.posJournalAmounts!.map(
                                (journal, index) => {
                                    const isSelected =
                                        selectedJournalId ===
                                        journal.posJournalId;
                                    const journalLabel = `${t(
                                        'tips.journal'
                                    )} ${String.fromCharCode(65 + index)}`;

                                    return (
                                        <JournalOption
                                            key={journal.posJournalId}
                                            className={
                                                isSelected ? 'selected' : ''
                                            }
                                            onClick={() =>
                                                handleJournalChange(
                                                    journal.posJournalId
                                                )
                                            }
                                        >
                                            <JournalInfo>
                                                <SMSemiBold color="#667085">
                                                    {journalLabel}
                                                </SMSemiBold>
                                            </JournalInfo>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <SMRegular color="text.secondary">
                                                    {getBalanceLabel(
                                                        journal.totalTips,
                                                        company?.currency
                                                    )}
                                                </SMRegular>
                                                <RadioButton
                                                    checked={isSelected}
                                                    onChange={() =>
                                                        handleJournalChange(
                                                            journal.posJournalId
                                                        )
                                                    }
                                                    size="small"
                                                    customRadio
                                                />
                                            </Box>
                                        </JournalOption>
                                    );
                                }
                            )}
                        </JournalList>
                    </Box>
                )}

                {!isRedistributeAll && (
                    <NumericInput
                        data-testid="new-tips-amount-input"
                        error={updateJournalAmount.isError}
                        value={newAmount ?? null}
                        disabled={hasMultipleJournals && !selectedJournalId}
                        placeholder={t('tips.enter_new_amount')}
                        onChange={(val) => {
                            setNewAmount(val ?? undefined);
                        }}
                        label={
                            hasMultipleJournals && !selectedJournalId
                                ? t('tips.select_journal_to_edit')
                                : hasMultipleJournals && selectedJournalId
                                  ? `${t('tips.new_tips_amount')} for ${t(
                                        'tips.journal'
                                    )} ${String.fromCharCode(
                                        65 +
                                            editTip.posJournalAmounts!.findIndex(
                                                (j) =>
                                                    j.posJournalId ===
                                                    selectedJournalId
                                            )
                                    )}`
                                  : t('tips.new_tips_amount')
                        }
                    />
                )}
                {updateJournalAmount.error || redistributeTipsForDate.error ? (
                    <SubTitle color={'error'}>
                        {t('tips.error_cant_distribute')}
                    </SubTitle>
                ) : (
                    <SubTitle>{t('tips.editing_before_payroll')}</SubTitle>
                )}
                <Box
                    m={'20px 0'}
                    display={'flex'}
                    justifyContent={'space-between'}
                >
                    <SecondaryButtonGrayLG
                        onClick={onCloseHandler}
                        sx={{ width: '48%' }}
                    >
                        <SMSemiBold>{t('cancel')}</SMSemiBold>
                    </SecondaryButtonGrayLG>

                    <PrimaryButtonLG
                        loading={
                            updateJournalAmount.isPending ||
                            redistributeTipsForDate.isPending
                        }
                        disabled={
                            isRedistributeAll
                                ? redistributeTipsForDate.isError
                                : newAmount === undefined ||
                                  updateJournalAmount.isError ||
                                  (hasMultipleJournals && !selectedJournalId)
                        }
                        onClick={() => {
                            if (isRedistributeAll) {
                                redistributeTipsForDate.mutate({
                                    posLocationId: editTip!.locationId,
                                    date: editTip!.date.format(
                                        DATE_FORMAT.SERVER_DATE
                                    )
                                });
                                return;
                            }
                            const journalId = hasMultipleJournals
                                ? selectedJournalId
                                : editTip?.posJournalId;
                            updateJournalAmount.mutate({
                                posJournalId: journalId as string,
                                newTotalAmount: newAmount!.toString()
                            });
                        }}
                        sx={{ width: '48%' }}
                    >
                        <SMSemiBold>{t('tips.redistribute')}</SMSemiBold>
                    </PrimaryButtonLG>
                </Box>
            </ConfirmationBox>
        </ModalContainer>
    );
};
